/* global Sentry */
/* global location */
import { history } from 'instantsearch.js/es/lib/routers'
import {
  getSEOPathFromRouteTable,
  getModifierFromLocation,
  updateCategoryHierarchyFromLocation
} from './seoRouting'
import { getTable, getAlgoliaIndexFromUrl, legacySortByToEngineValue } from './helpers'
import { primaryIndexName } from '../../libs/search/searchClient'
import { multiSelectCategoriesEnabled, pageTrailPath } from './categoryPaths'
import {
  isRoomTypePage,
  isSeoLocationRoute
} from '../urlHelpers'
import { stripLocaleFromPath, getLocaleFromPath, prependLocale } from '../localeHelpers'

function getQueryParametersFromRoutes (routeState) {
  const params = {
    dFR: {
      age_group_name: routeState.age_group_name,
      vibe_names: routeState.vibe_names,
      color_name: routeState.color_name,
      brand_name: routeState.brand_name,
      year_of_manufacture: routeState.year_of_manufacture,
      designed_by_name: routeState.designed_by_name,
      main_material_name: routeState.main_material_name,
      country_from_address: routeState.country_from_address,
      address_city: routeState.address_city,
      user_slug: routeState.user_slug,
      // TODO: Remove later - Save Search / shared links
      free_uk_delivery_for_algolia: routeState.free_uk_delivery_for_algolia,
      free_london_delivery_for_algolia: routeState.free_london_delivery_for_algolia,
      free_collection: routeState.free_collection,
      eu_delivery_pilot: routeState.eu_delivery_pilot,
      professionally_reupholstered: routeState.professionally_reupholstered,
      reupholstery_available: routeState.reupholstery_available,
      professionally_refurbished: routeState.professionally_refurbished,
      professionally_cleaned: routeState.professionally_cleaned,
      certificate_of_authenticity: routeState.certificate_of_authenticity,
      signed_by_artist: routeState.signed_by_artist,
      sustainable_packaging: routeState.sustainable_packaging,
      in_time_for_christmas_for_algolia: routeState.in_time_for_christmas_for_algolia,
      sale: routeState.sale,
      top_score_seller: routeState.top_score_seller,
      professional_seller: routeState.professional_seller,
      number_of_doors: routeState.number_of_doors,
      number_of_drawers: routeState.number_of_drawers,
      number_of_shelves: routeState.number_of_shelves,
      number_of_seats: routeState.number_of_seats,
      door_type_name: routeState.door_type_name,
      shape_name: routeState.shape_name,
      feature_names: routeState.feature_names,
      seating_shape_names: routeState.seating_shape_names,
      orientation_name: routeState.orientation_name,
      art_size_name: routeState.art_size_name,
      art_subject_names: routeState.art_subject_names,
      artistic_movement_names: routeState.artistic_movement_names,
      set_of: routeState.set_of,
      commercially_accessible: routeState.commercially_accessible,
      luxury_items: routeState.luxury_items,
      category_paths: routeState.category_paths
    },
    nR: {
      price: routeState.price,
      width_float: routeState.width_float,
      height_float: routeState.height_float,
      depth_float: routeState.depth_float,
      sale_percentage_int: routeState.sale_percentage_int
    },
    hFR: {
      'category_hierarchy.lvl0': routeState['category_hierarchy.lvl0']
    },
    other: {
      sortBy: routeState.sortBy,
      hitsPerPage: routeState.hitsPerPage,
      q: encodeURIComponent(routeState.q)
    }
  }

  if (routeState._tags) params.tR = routeState._tags
  if (routeState.p !== 1) params.other.p = routeState.p
  if (routeState.similar_query) params.other.similar_query = routeState.similar_query
  if (routeState.cross_category) params.other.cross_category = routeState.cross_category

  return params
}

function removeAttributeFromQueryParameter (queryParameters, attributes) {
  attributes.forEach((key) => {
    if (key === 'category_hierarchy.lvl0') {
      delete queryParameters.hFR[key]
    } else {
      // it's going to be a modifier like color, so lets remove from dFR
      // NOTE: if we remove the modifier from dFR to hFR or something else, this will be a problem...
      // it's a problem when its a problem
      delete queryParameters.dFR[key]
    }
  })
  return queryParameters
}

// All state is added to the query params as this is a non SEO hierarchical route
function parametersToQueryString (queryParameters, qsModule) {
  const queryString = qsModule.stringify({ ...queryParameters.other, dFR: queryParameters.dFR, hFR: queryParameters.hFR, nR: queryParameters.nR, tR: queryParameters.tR }, {
    arrayFormat: 'indices',
    delimiter: '&',
    addQueryPrefix: true
  })
  // if query string has nothing in there, lets be nice and not show anything
  return queryString === '?q=' ? '' : queryString
}

function normalizeSeoUrl (seoUrl) {
  const withLeadingSlash = seoUrl.startsWith('/') ? seoUrl : '/' + seoUrl
  const stripped = stripLocaleFromPath(withLeadingSlash)
  const base = stripped.substring(1)

  return isRoomTypePage(stripped)
    ? base.split('/').slice(2).join('/')
    : base
}

// ["art": ["style/african"], "art/wall-art": ["color/grey", "color/gold"]]
// NOTE: algolia will pass in stuff from the route state as proper case i.e. Grey or Yellow something
// we need to lower case and add a - instead of space to match the modifiers in the modifier doc
function getModifierUrlFromState (rawSeoUrl, routeState) {
  const seoUrl = normalizeSeoUrl(rawSeoUrl)

  const modifierLookup = getTable('modifiers')
  const potentialRouteModifiers = getTable('path-modifiers')[seoUrl]
  let matchedModifierUrl = null
  let matchedModifierKey = null

  // if route does not have any modifiers, we will not be adding any to the url, break early
  if (!potentialRouteModifiers || potentialRouteModifiers.length === 0) return [matchedModifierUrl, matchedModifierKey]

  // modifiers in doc have presedence on what is assigned first
  for (const modifierUrl of potentialRouteModifiers) {
    const stateLookupKey = modifierUrl.split('/')[0]
    const stateModifierKey = modifierLookup[stateLookupKey]

    if (!routeState[stateModifierKey] || routeState[stateModifierKey].length !== 1) continue

    // Replace any special characters (except `&`)) in the value of
    // the modifier found with a hyphen unless it's a `'` or `a`, then it is removed.
    // e.g. J.L. Moller -> jl-moller, G Plan -> g-plan Fox & Hound -> fox-&-hound
    const slugifiedModifierValue = routeState[stateModifierKey][0].replace(/['.]/g, '').replace(/[^a-zA-Z0-9&]+/g, '-').toLowerCase()
    const stateLookupValue = `${stateLookupKey}/${slugifiedModifierValue}`

    if (modifierUrl !== stateLookupValue) continue

    matchedModifierUrl = `${seoUrl}/${modifierUrl}`
    matchedModifierKey = stateModifierKey

    return [matchedModifierUrl, stateModifierKey]
  }

  return [matchedModifierUrl, matchedModifierKey]
}

function updateAttributesFromModifierLookup (queryParameters, modifierKey, modifierValue) {
  if (modifierKey === null || modifierValue === null) return // make no change to queryParameters
  queryParameters[modifierKey] = [modifierValue]
}

const router = history({
  // Starting from the next major version, InstantSearch will not clean up the URL from active refinements when it is disposed.
  // We recommend setting `cleanUrlOnDispose` to false to adopt this change today.
  // To stay with the current behaviour and remove this warning, set the option to true.
  // See documentation: https://www.algolia.com/doc/api-reference/widgets/history-router/js/#widget-param-cleanurlondispose
  cleanUrlOnDispose: true,
  createURL ({ qsModule, routeState, location }) {
    const { hostname, href, pathname, protocol, port = '' } = location
    const portWithPrefix = port === '' ? '' : `:${port}`
    const currentLocale = getLocaleFromPath(pathname)
    const strippedPathname = stripLocaleFromPath(pathname)
    const pathWithoutSlash = strippedPathname.substring(1)
    const matchedSearchUrl = href.match(/^(.*?)\/search/g)
    const rootUrl = `${protocol}//${hostname}${portWithPrefix}/`
    const queryParameters = getQueryParametersFromRoutes(routeState) // N.B: Despite const, values inside this object are updated (mutated) below

    // When the only selected category is the page's own, the path already says
    // it — drop the redundant dFR param. Runs per generated href, so cheap
    // checks first; compare case-insensitively (facet casing vs slug casing).
    const selections = queryParameters.dFR.category_paths
    if (selections && selections.length === 1 && multiSelectCategoriesEnabled()) {
      const trail = pageTrailPath(location)
      if (trail && selections[0].toLowerCase() === trail.toLowerCase()) {
        delete queryParameters.dFR.category_paths
      }
    }
    const isSellerSlug = href.match(/^(.*?)\/sellers/g)
    const isCollectionPage = href.match(/^(.*?)\/collections/g)

    if (isSellerSlug) delete queryParameters.dFR.user_slug

    if (isRoomTypePage(href) || isCollectionPage || (queryParameters.other.q === '' && queryParameters.other.p)) {
      delete queryParameters.other.q
    }

    const queryString = parametersToQueryString(queryParameters, qsModule)

    const baseUrl =
      (matchedSearchUrl && matchedSearchUrl[0]) || `${rootUrl}${pathWithoutSlash}`

    const isContentRoute = isSeoLocationRoute(location)
    if (!isContentRoute && !matchedSearchUrl) {
      return `${rootUrl.slice(0, -1)}${prependLocale('/' + pathWithoutSlash, currentLocale)}${queryString}`
    }
    if (!isContentRoute) {
      return `${baseUrl}${queryString}`
    }

    const contentRoutePath = getSEOPathFromRouteTable(routeState, getTable('path-hierarchy'), pathWithoutSlash)
    if (contentRoutePath === null) { // this should never happen??? - UPDATE in reality this happens all the time!
      console.error('seo_route_not_found', routeState)
      return `${rootUrl.slice(0, -1)}${prependLocale('/' + pathWithoutSlash, currentLocale)}${queryString}`
    }

    // get modifier from route, and do a lookup to see if that path should be used instead
    const [modifierUrl, modifierToRemove] = getModifierUrlFromState(contentRoutePath, routeState)
    const attributesToRemove = ['category_hierarchy.lvl0']

    if (modifierUrl) {
      removeAttributeFromQueryParameter(queryParameters, [...attributesToRemove, modifierToRemove])
      const cleanedQueryString = parametersToQueryString(queryParameters, qsModule)

      if (isRoomTypePage(strippedPathname)) {
        const roomTypeSegments = pathWithoutSlash.split('/').slice(0, 2).join('/')
        return `${rootUrl.slice(0, -1)}${prependLocale('/' + roomTypeSegments + '/' + modifierUrl, currentLocale)}${cleanedQueryString}`
      } else {
        return `${rootUrl.slice(0, -1)}${prependLocale('/' + modifierUrl, currentLocale)}${cleanedQueryString}`
      }
    } else {
      // if no modifier url, return the normal seo url with the queryString cleaned up
      // of product type, category name, subcategory name
      removeAttributeFromQueryParameter(queryParameters, attributesToRemove)
      const cleanedQueryString = parametersToQueryString(queryParameters, qsModule)
      return `${rootUrl.slice(0, -1)}${prependLocale('/' + contentRoutePath, currentLocale)}${cleanedQueryString}`
    }
  },

  parseURL ({ qsModule, location }) {
    const queryString = decodeURI(location.search.slice(1).replace(/dFR/g, '').replace(/nR/g, '').replace(/hFR/g, ''))
    const isContentRoute = isSeoLocationRoute(location)
    // qs only parses indexed params into an array up to arrayLimit (default 20!);
    // beyond that it returns an object with numeric keys, which crashes
    // connectRefinementList. 1000 covers any URL short enough to reach the app.
    const queryParameters = qsModule.parse(queryString, { arrayLimit: 1000 })

    translateV2QueriesToV3(queryParameters)

    const strippedPathname = stripLocaleFromPath(location.pathname)

    if (isContentRoute) {
      // has url got category, product type, modifier? pull them each from the route
      // by constructing the route

      const allRoutes = []
      if (isRoomTypePage(strippedPathname)) {
        allRoutes.push(...strippedPathname.split('/').filter(String).slice(2))
      } else {
        allRoutes.push(...strippedPathname.split('/').filter(String))
      }

      const [modifierIndex, modifierKey, modifierValue] = getModifierFromLocation(allRoutes, getTable('modifiers'))
      updateAttributesFromModifierLookup(queryParameters, modifierKey, modifierValue)
      updateCategoryHierarchyFromLocation(modifierIndex, allRoutes, queryParameters)
    }

    // A dFR[category_paths] param fully defines the category filter (the path
    // is dressing only). Without one, legacy state still applies: the SEO
    // path's trail (segment array, pre-ticked) or an old hFR param (full-path
    // string, saved-search URLs) — the widget that consumed hFR isn't mounted
    // under the flag, so unconverted state would be silently lost.
    if (multiSelectCategoriesEnabled() && !queryParameters.category_paths) {
      const hierarchy = queryParameters['category_hierarchy.lvl0']
      if (hierarchy && hierarchy.length > 0) {
        queryParameters.category_paths = [[].concat(hierarchy).join(' > ')]
      }
    }

    const params = new Proxy(new URLSearchParams(location.search), {
      get: (searchParams, prop) => searchParams.get(prop)
    })

    return {
      q: decodeURIComponent(''),
      similar_query: params.similar_query,
      cross_category: params.cross_category,
      ...queryParameters
    }
  }
})

const stateMapping = {
  stateToRoute (uiState) {
    const params = new Proxy(new URLSearchParams(location.search), {
      get: (searchParams, prop) => searchParams.get(prop)
    })
    const similarQuery = params.similar_query
    const crossCategory = params.cross_category
    const indexName = Object.keys(uiState)[0] // we only ever use one index at a time (not federated)
    const indexUiState = uiState[indexName]
    const {
      query, page, hierarchicalMenu, refinementList, toggle, range, sortBy, hitsPerPage, numericMenu
    } = indexUiState

    return {
      q: query || '',
      p: page,
      similar_query: similarQuery,
      cross_category: crossCategory,
      'category_hierarchy.lvl0': hierarchicalMenu && hierarchicalMenu['category_hierarchy.lvl0'],
      age_group_name: refinementList && refinementList.age_group_name,
      vibe_names: refinementList && refinementList.vibe_names,
      color_name: refinementList && refinementList.color_name,
      brand_name: refinementList && refinementList.brand_name,
      year_of_manufacture: refinementList && refinementList.year_of_manufacture,
      designed_by_name: refinementList && refinementList.designed_by_name,
      main_material_name: refinementList && refinementList.main_material_name,
      country_from_address: refinementList && refinementList.country_from_address,
      address_city: refinementList && refinementList.address_city,
      // TODO: Consider removing later
      free_uk_delivery_for_algolia: toggle && toggle.free_uk_delivery_for_algolia,
      free_london_delivery_for_algolia: toggle && toggle.free_london_delivery_for_algolia,
      free_collection: toggle && toggle.free_collection,
      eu_delivery_pilot: toggle && toggle.eu_delivery_pilot,
      professionally_reupholstered: toggle && toggle.professionally_reupholstered,
      reupholstery_available: toggle && toggle.reupholstery_available,
      professionally_refurbished: toggle && toggle.professionally_refurbished,
      professionally_cleaned: toggle && toggle.professionally_cleaned,
      certificate_of_authenticity: toggle && toggle.certificate_of_authenticity,
      signed_by_artist: toggle && toggle.signed_by_artist,
      sustainable_packaging: toggle && toggle.sustainable_packaging,
      in_time_for_christmas_for_algolia: toggle && toggle.in_time_for_christmas_for_algolia,
      sale: toggle && toggle.sale,
      top_score_seller: toggle && toggle.top_score_seller,
      professional_seller: toggle && toggle.professional_seller,
      trade_price_and_trade_price_under_sale_price: toggle && toggle.trade_price_and_trade_price_under_sale_price,
      price: range && (range.display_price_for_algolia || range.display_price_trade_user_for_algolia),
      width_float: range && range.width_float,
      height_float: range && range.height_float,
      depth_float: range && range.depth_float,
      sale_percentage_int: numericMenu && numericMenu.sale_percentage_int,
      sortBy: sortBy,
      user_slug: refinementList && refinementList.user_slug,
      number_of_doors: refinementList && refinementList.number_of_doors,
      number_of_drawers: refinementList && refinementList.number_of_drawers,
      number_of_shelves: refinementList && refinementList.number_of_shelves,
      number_of_seats: refinementList && refinementList.number_of_seats,
      door_type_name: refinementList && refinementList.door_type_name,
      shape_name: refinementList && refinementList.shape_name,
      feature_names: refinementList && refinementList.feature_names,
      seating_shape_names: refinementList && refinementList.seating_shape_names,
      orientation_name: refinementList && refinementList.orientation_name,
      art_size_name: refinementList && refinementList.art_size_name,
      art_subject_names: refinementList && refinementList.art_subject_names,
      artistic_movement_names: refinementList && refinementList.artistic_movement_names,
      set_of: refinementList && refinementList.set_of,
      commercially_accessible: refinementList && refinementList.commercially_accessible,
      luxury_items: refinementList && refinementList.luxury_items,
      category_paths: refinementList && refinementList.category_paths,
      hitsPerPage:
        (hitsPerPage && String(hitsPerPage)) || undefined,
      _tags:
        refinementList && refinementList._tags
    }
  },

  routeToState (routeState) {
    // MUST match the indexName the InstantSearch instance was created with (see
    // search.js), not the raw Algolia index name. InstantSearch keys uiState by
    // index, so if these disagree the parsed route state is silently dropped —
    // which on an SEO route means the category refinement never applies and the
    // PLP shows unfiltered results with no error anywhere.
    const indexName = primaryIndexName(getAlgoliaIndexFromUrl())
    return {
      [indexName]: {
        query: routeState.q,
        page: routeState.p,
        similar_query: routeState.similar_query,
        cross_category: routeState.cross_category,
        hierarchicalMenu: {
          'category_hierarchy.lvl0': routeState['category_hierarchy.lvl0']
        },
        refinementList: {
          age_group_name: routeState.age_group_name,
          vibe_names: routeState.vibe_names,
          color_name: routeState.color_name,
          brand_name: routeState.brand_name,
          year_of_manufacture: routeState.year_of_manufacture,
          designed_by_name: routeState.designed_by_name,
          main_material_name: routeState.main_material_name,
          country_from_address: routeState.country_from_address,
          address_city: routeState.address_city,
          user_slug: routeState.user_slug,
          number_of_doors: routeState.number_of_doors,
          number_of_drawers: routeState.number_of_drawers,
          number_of_shelves: routeState.number_of_shelves,
          number_of_seats: routeState.number_of_seats,
          door_type_name: routeState.door_type_name,
          shape_name: routeState.shape_name,
          feature_names: routeState.feature_names,
          seating_shape_names: routeState.seating_shape_names,
          orientation_name: routeState.orientation_name,
          art_size_name: routeState.art_size_name,
          art_subject_names: routeState.art_subject_names,
          artistic_movement_names: routeState.artistic_movement_names,
          set_of: routeState.set_of,
          category_paths: routeState.category_paths,
          _tags: buildTags(routeState._tags)
        },
        toggle: {
          // TODO: Consider removing later
          free_uk_delivery_for_algolia: routeState.free_uk_delivery_for_algolia,
          free_london_delivery_for_algolia: routeState.free_london_delivery_for_algolia,
          free_collection: routeState.free_collection,
          eu_delivery_pilot: routeState.eu_delivery_pilot,
          professionally_reupholstered: routeState.professionally_reupholstered,
          reupholstery_available: routeState.reupholstery_available,
          professionally_refurbished: routeState.professionally_refurbished,
          professionally_cleaned: routeState.professionally_cleaned,
          certificate_of_authenticity: routeState.certificate_of_authenticity,
          signed_by_artist: routeState.signed_by_artist,
          sustainable_packaging: routeState.sustainable_packaging,
          in_time_for_christmas_for_algolia: routeState.in_time_for_christmas_for_algolia,
          sale: routeState.sale,
          top_score_seller: routeState.top_score_seller,
          professional_seller: routeState.professional_seller,
          trade_price_and_trade_price_under_sale_price: routeState.trade_price_and_trade_price_under_sale_price,
          commercially_accessible: routeState.commercially_accessible,
          luxury_items: routeState.luxury_items
        },
        range: {
          display_price_for_algolia: routeState.price,
          display_price_trade_user_for_algolia: routeState.price,
          width_float: routeState.width_float,
          height_float: routeState.height_float,
          depth_float: routeState.depth_float
        },
        numericMenu: {
          sale_percentage_int: routeState.sale_percentage_int
        },
        // Not passed through: sortBy is an index name, and legacy URLs carry
        // the other engine's. Translated for the running engine, anything
        // unrecognised falls back to relevance (legacySortByToEngineValue).
        sortBy: legacySortByToEngineValue(routeState.sortBy, indexName),
        hitsPerPage: Number(routeState.hitsPerPage)
      }
    }
  }
}

function buildTags (tags) {
  try {
    return (tags ? tags.split(',') : [])
  } catch (e) {
    try {
      return tags
    } catch (e) {
      const message = 'The tags used at this stage are: ' + tags + ' and the typeof tags is ' + typeof tags + ' and its constructor is ' + tags.constructor
      throw new Error(message)
    }
  }
}

// there were breaking changes when we did upgrade from v2 to v3 instantsearch
// pages used to be 0 indexed as first page is p=0 and in v3 first page is p=1
// range (between 100 and 200) in v2 used to look like &nR[price][<%3D][0]=200&nR[price][>%3D][0]=100
// same range in v3 needs to be nR[price]=100%3A200

function translateV2QueriesToV3 (queryParameters) {
  // TODO: I (Joj) suspect that we never pass a page param at all for first pages now.
  // i.e: queryParameters.p will only be defined if >= 2, otherwise undefined.
  // If we can test and confirm we can remove the following three lines.
  if (queryParameters.p && queryParameters.p === '0') {
    Sentry.captureMessage('translateV2QueriesToV3 queryParameters.p === "0", from: ' + document.referrer)
    queryParameters.p = '1'
  }
  const rangeKeys = ['price', 'width_float', 'height_float', 'depth_float', 'sale_percentage_int']

  rangeKeys.forEach((key) => {
    if (queryParameters[key] && queryParameters[key]['<='] && queryParameters[key]['>=']) {
      queryParameters[key] = `${queryParameters[key]['>=']}:${queryParameters[key]['<=']}`
    }
  })

  if (queryParameters.tR && queryParameters.tR.length > 0) {
    queryParameters._tags = queryParameters.tR.toString()
  }

  return queryParameters
}

const searchRouting = {
  router,
  stateMapping
}

export default searchRouting
